import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis,
  ReferenceLine, Tooltip as RTooltip, AreaChart, Area,
  BarChart, Bar, CartesianGrid,
} from 'recharts';
import portfolioService, { RiskData, RiskCharts as RiskChartsData } from '../../services/portfolio';
import InfoTooltip from '../ui/InfoTooltip';

interface Props {
  portfolioId: number;
}

const NAVY = '#000537';
const BLUE = '#8c9fd4';
const LIGHT = '#dce3f0';      // faint achievable-region cloud
const GREY = '#a1a1aa';       // markers (min-var, max-Sharpe, benchmark), VaR lines
const FRONTIER = '#d4d4d8';   // efficient-frontier line (very light grey)
const GOLD = '#ca8a04';       // average-return accent (daily-returns chart)

const axisPct = (v: number) => `${v.toFixed(0)}%`;

const ChartCard: React.FC<{ title: string; tip?: React.ReactNode; right?: string; control?: React.ReactNode; children: React.ReactNode }> =
  ({ title, tip, right, control, children }) => {
    // Lift the whole card above its siblings while the info popup is open, so the
    // tooltip (which overflows downward) isn't painted behind the next row's cards.
    const [tipOpen, setTipOpen] = useState(false);
    return (
      <div className="card card-forest h-100" style={{ position: 'relative', zIndex: tipOpen ? 50 : undefined }}>
        <div className="card-body p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="fw-semibold" style={{ fontSize: '0.95rem', color: NAVY }}>{title}</span>
            <span className="d-flex align-items-center" style={{ fontSize: '0.75rem', color: '#a1a1aa', gap: 8 }}>
              {right}
              {control}
              {tip && <InfoTooltip text={tip} width={420} placement="bottom" onShow={setTipOpen} />}
            </span>
          </div>
          {children}
        </div>
      </div>
    );
  };

// Subtle grey-on-grey segmented toggle (selected = light grey, not navy).
const ZoomToggle: React.FC<{ zoom: boolean; setZoom: (v: boolean) => void }> = ({ zoom, setZoom }) => (
  <span style={{ display: 'inline-flex', border: '1px solid #ececee', borderRadius: 6, overflow: 'hidden' }}>
    {([['Zoom', true], ['Full', false]] as [string, boolean][]).map(([label, val]) => (
      <button key={label} type="button" onClick={() => setZoom(val)}
        style={{
          border: 'none', cursor: 'pointer', fontSize: '0.66rem', padding: '1px 8px',
          background: zoom === val ? '#f4f4f5' : 'transparent',
          color: zoom === val ? '#52525b' : '#a1a1aa',
          fontWeight: zoom === val ? 600 : 400,
        }}>
        {label}
      </button>
    ))}
  </span>
);

// Human "x ago" from an ISO timestamp (the cache's computed_at).
const timeAgo = (iso?: string): string => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return 'just now';
  const m = Math.round(s / 60);
  if (m < 90) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 36) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

// Force a full server-side risk recompute (busts result + price-series cache).
const RecomputeButton: React.FC<{ pending: boolean; computedAt?: string; onClick: () => void }> =
  ({ pending, computedAt, onClick }) => (
    <button type="button" onClick={onClick} disabled={pending}
      title={pending ? 'Recomputing…' : `Recompute risk${computedAt ? ` · computed ${timeAgo(computedAt)}` : ''}`}
      style={{
        display: 'inline-flex', alignItems: 'center', border: '1px solid #ececee',
        borderRadius: 6, background: 'transparent', cursor: pending ? 'default' : 'pointer',
        color: '#a1a1aa', padding: '3px 5px',
      }}>
      <style>{'@keyframes risk-spin{to{transform:rotate(360deg)}}'}</style>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
        style={{ transformOrigin: 'center', animation: pending ? 'risk-spin 0.8s linear infinite' : undefined }}>
        <path d="M21 12a9 9 0 1 1-2.64-6.36M21 4v5h-5" stroke="currentColor"
          strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );

// --- Markowitz risk–return scatter (decluttered) ---------------------------
const MarkowitzScatter: React.FC<{ charts: RiskChartsData; zoom: boolean }> = ({ charts, zoom }) => {

  const toPct = (p: { volatility: number; annualized_return?: number; ret?: number }) => ({
    x: p.volatility * 100,
    y: ((p.annualized_return ?? p.ret) as number) * 100,
  });

  const rawCloud = (charts.frontier?.cloud ?? []).map(toPct);
  const curve = (charts.frontier?.curve ?? []).map(toPct);   // analytic efficient frontier
  const instruments = charts.instruments.map(i => ({ ...toPct(i), label: i.name || i.identifier }));
  const portfolio = [{ ...toPct(charts.portfolio), label: 'Portfolio' }];
  const benchmark = charts.benchmark ? [{ ...toPct(charts.benchmark), label: 'Benchmark' }] : [];
  const maxSharpe = charts.frontier ? [{ ...toPct(charts.frontier.max_sharpe), label: 'Max Sharpe' }] : [];
  const minVar = charts.frontier ? [{ ...toPct(charts.frontier.min_variance), label: 'Min variance' }] : [];

  // Zoom view: the visible window is driven *only* by the named points
  // (instruments / portfolio / benchmark / frontier) — never the random cloud.
  // Otherwise a single extreme-volatility holding stretches the cloud across the
  // whole axis and the clip never bites. The cloud is then trimmed to that window.
  const focus = [...instruments, ...portfolio, ...benchmark, ...maxSharpe, ...minVar];
  const anchorX = [...portfolio, ...benchmark].map(p => p.x);
  const anchorY = [...portfolio, ...benchmark].map(p => p.y);
  const round = (v: number) => Math.round(v * 10) / 10;
  let xDomain: [number | string, number | string] = ['dataMin', 'dataMax'];
  let yDomain: [number | string, number | string] = ['dataMin', 'dataMax'];
  let cloud = rawCloud.filter((_, i) => i % 4 === 0);
  if (zoom && focus.length) {
    const xs = focus.map(p => p.x).sort((a, b) => a - b);
    const ys = focus.map(p => p.y).sort((a, b) => a - b);
    const xMax = Math.max(quantile(xs, 0.85), ...anchorX);
    const yLo = Math.min(quantile(ys, 0.05), ...anchorY);
    const yHi = Math.max(quantile(ys, 0.95), ...anchorY);
    const padX = (xMax - xs[0]) * 0.12 || 1;
    const padY = (yHi - yLo) * 0.12 || 1;
    const x0 = Math.max(0, round(xs[0] - padX)), x1 = round(xMax + padX);
    const y0 = round(yLo - padY), y1 = round(yHi + padY);
    xDomain = [x0, x1];
    yDomain = [y0, y1];
    cloud = cloud.filter(p => p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1);
  }

  return (
      <ResponsiveContainer width="100%" height={300}>
      <ScatterChart margin={{ top: 8, right: 12, bottom: 18, left: -4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
        <XAxis type="number" dataKey="x" name="Volatility" tickFormatter={axisPct}
          tick={{ fontSize: 10, fill: '#71717a' }} domain={xDomain} allowDataOverflow={zoom}
          label={{ value: 'Volatility', position: 'insideBottom', offset: -8, fontSize: 10, fill: '#a1a1aa' }} />
        <YAxis type="number" dataKey="y" name="Return" tickFormatter={axisPct}
          tick={{ fontSize: 10, fill: '#71717a' }} width={40} domain={yDomain} allowDataOverflow={zoom}
          label={{ value: 'Return', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#a1a1aa' }} />
        <RTooltip cursor={{ strokeDasharray: '3 3' }}
          content={({ active, payload }: any) => {
            if (!active || !payload?.length) return null;
            const d = payload[0].payload;
            return (
              <div style={{ background: '#fff', border: '1px solid #f4f4f5', borderRadius: 8, padding: '6px 10px', fontSize: '0.75rem', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                {d.label && <div style={{ fontWeight: 600, color: '#18181b' }}>{d.label}</div>}
                <div style={{ color: '#71717a' }}>σ {d.x.toFixed(1)}% · r {d.y.toFixed(1)}%</div>
              </div>
            );
          }} />
        <Scatter data={cloud} fill={LIGHT} fillOpacity={0.22} isAnimationActive={false} />
        <Scatter data={curve} fill={FRONTIER} line={{ stroke: FRONTIER, strokeWidth: 1.6 }}
          lineJointType="monotone" shape={() => <g />} isAnimationActive={false} />
        <Scatter data={instruments} fill={BLUE} fillOpacity={0.85} isAnimationActive={false} />
        <Scatter data={minVar} fill={GREY} shape="cross" isAnimationActive={false} />
        <Scatter data={maxSharpe} fill={GREY} shape="star" isAnimationActive={false} />
        <Scatter data={benchmark} fill={GREY} shape="diamond" isAnimationActive={false} />
        <Scatter data={portfolio} fill={NAVY} shape="triangle" isAnimationActive={false} />
      </ScatterChart>
    </ResponsiveContainer>
  );
};

// --- Underwater drawdown curve ---------------------------------------------
const DrawdownChart: React.FC<{ charts: RiskChartsData }> = ({ charts }) => {
  const data = charts.dates.map((d, i) => ({ date: d, dd: charts.drawdown[i] * 100 }));
  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 18, left: -4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#a1a1aa' }} minTickGap={40}
          tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })} />
        <YAxis tick={{ fontSize: 10, fill: '#71717a' }} width={40} tickFormatter={(v) => `${v.toFixed(0)}%`} />
        <RTooltip labelFormatter={(d) => new Date(d as string).toLocaleDateString()}
          formatter={(v: any) => [`${(v as number).toFixed(1)}%`, 'Drawdown']} />
        <Area type="monotone" dataKey="dd" stroke={NAVY} fill={NAVY} fillOpacity={0.1} strokeWidth={1.5} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// --- Daily return distribution with VaR markers ----------------------------
const quantile = (sorted: number[], q: number) => {
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
};

const DistributionChart: React.FC<{ charts: RiskChartsData }> = ({ charts }) => {
  const r = charts.daily_returns;
  if (r.length < 2) return null;
  const sorted = [...r].sort((a, b) => a - b);
  const q05 = quantile(sorted, 0.05) * 100;
  const q01 = quantile(sorted, 0.01) * 100;
  const mean = (r.reduce((a, b) => a + b, 0) / r.length) * 100;

  // Robust x-range: clip to a Tukey-style IQR fence so a single bad price tick
  // (e.g. a spurious +58% day from a Yahoo data glitch) can't stretch the axis
  // and collapse every real day into one bar. Days beyond the fence are dropped
  // from the bars; the VaR markers stay computed from the full sample and remain
  // in view (the left edge is widened to include VaR 99% if needed).
  const q25 = quantile(sorted, 0.25);
  const q75 = quantile(sorted, 0.75);
  const iqr = (q75 - q25) || (sorted[sorted.length - 1] - sorted[0]) || 0.01;
  const loFence = Math.max(sorted[0], q25 - 4 * iqr) * 100;
  const hiFence = Math.min(sorted[sorted.length - 1], q75 + 4 * iqr) * 100;
  const min = Math.min(loFence, q01);
  const max = hiFence;

  const BINS = 24;
  const width = (max - min) / BINS || 1;
  const bins = Array.from({ length: BINS }, (_, i) => ({ center: min + width * (i + 0.5), count: 0 }));
  r.forEach(v => {
    const pct = v * 100;
    if (pct < min || pct > max) return;   // drop fenced outliers from the bars
    const idx = Math.min(BINS - 1, Math.max(0, Math.floor((pct - min) / width)));
    bins[idx].count += 1;
  });
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={bins} margin={{ top: 22, right: 18, bottom: 18, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
        <XAxis dataKey="center" type="number" domain={[min, max]} tick={{ fontSize: 10, fill: '#a1a1aa' }}
          tickFormatter={(v) => `${v.toFixed(1)}%`} />
        <YAxis tick={{ fontSize: 10, fill: '#71717a' }} width={36} allowDecimals={false} />
        <RTooltip formatter={(v: any) => [v, 'Days']} labelFormatter={(v) => `${(v as number).toFixed(2)}% daily`} />
        <ReferenceLine x={q05} stroke={GREY} strokeDasharray="4 3" label={{ value: 'VaR 95%', fontSize: 9, fill: GREY, position: 'top' }} />
        <ReferenceLine x={q01} stroke={GREY} strokeDasharray="4 3" label={{ value: '99%', fontSize: 9, fill: GREY, position: 'top' }} />
        <ReferenceLine x={mean} stroke={GOLD} strokeWidth={1.5} label={{ value: `Avg ${mean.toFixed(2)}%`, fontSize: 9, fill: GOLD, position: 'top' }} />
        <Bar dataKey="count" fill={NAVY} fillOpacity={0.85} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const RiskCharts: React.FC<Props> = ({ portfolioId }) => {
  const [zoom, setZoom] = useState(true);
  const queryClient = useQueryClient();
  const { data } = useQuery<RiskData>({
    queryKey: ['portfolio-risk', portfolioId],
    queryFn: () => portfolioService.getPortfolioRisk(portfolioId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Force-recompute shares the ['portfolio-risk'] cache key with the risk table
  // and correlation card, so writing the fresh result refreshes all three.
  const recompute = useMutation({
    mutationFn: () => portfolioService.recomputePortfolioRisk(portfolioId),
    onSuccess: (fresh) => queryClient.setQueryData(['portfolio-risk', portfolioId], fresh),
  });

  const charts = data?.charts;
  if (!charts) return null;

  const windowLabel = `${charts.window_years}Y`;

  return (
    <div className="row g-4" style={{ marginTop: '18px' }}>
      <div className="col-12 col-lg-4">
        <ChartCard
          title="Risk–return"
          right={windowLabel}
          control={<>
            <RecomputeButton pending={recompute.isPending} computedAt={data?.computed_at} onClick={() => recompute.mutate()} />
            <ZoomToggle zoom={zoom} setZoom={setZoom} />
          </>}
          tip={<>
            <div>The Markowitz risk–return view. Each point is plotted by its annualized volatility (x) and return (y).</div>
            <div style={{ marginTop: 6 }}>The <span style={{ color: BLUE }}>faint blue cloud</span> is the <strong>achievable region</strong>: we draw thousands of random long-only weightings of your instruments (each a weight vector <em>w</em> ≥ 0 summing to 1), then score each mix on the instruments' historical covariance over the window:</div>
            <div style={{ marginTop: 4, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.72rem', color: NAVY, background: '#f4f4f5', borderRadius: 4, padding: '4px 7px' }}>
              σ_p = √(wᵀ Σ w) · √252&nbsp;&nbsp;&nbsp;r_p = (wᵀ µ) · 252
            </div>
            <div style={{ marginTop: 4 }}>where µ is the vector of mean daily returns and Σ their daily covariance matrix (both annualized by 252 trading days). Together the mixes trace the set of portfolios you could build from exactly these holdings.</div>
            <div style={{ marginTop: 6 }}>The solid <span style={{ color: GREY }}>grey line</span> is the <strong>efficient frontier</strong> — the cloud's upper-left boundary, computed properly by long-only mean-variance optimization (for each target risk, the mix with the highest return). The <span style={{ color: GREY }}>min-variance</span> and <span style={{ color: GREY }}>max-Sharpe</span> points sit on it.</div>
            <div style={{ marginTop: 6, color: '#71717a' }}>Computed server-side (deterministic) and cached; refreshes automatically when you add or remove a holding, or via <strong>Recompute</strong>.</div>
            <div style={{ marginTop: 6 }}>
              <span style={{ color: NAVY }}>▲</span> <strong>Portfolio</strong> — your current book ·{' '}
              <span style={{ color: GREY }}>◆</span> <strong>Benchmark</strong> ({data?.benchmark ?? 'ACWI'}) ·{' '}
              <span style={{ color: BLUE }}>●</span> <strong>Instruments</strong> — each ETF/stock you hold ·{' '}
              <span style={{ color: GREY }}>★</span> <strong>Max-Sharpe</strong> — best risk-adjusted mix ·{' '}
              <span style={{ color: GREY }}>✚</span> <strong>Min-variance</strong> — lowest-risk mix
            </div>
            <div style={{ marginTop: 6, color: '#71717a' }}>Historical and in-sample — the return axis is illustrative, not predictive. <strong>Zoom</strong> clips the axes to the bulk of the points (hiding extreme-volatility outliers); <strong>Full</strong> shows everything.</div>
          </>}
        >
          <MarkowitzScatter charts={charts} zoom={zoom} />
        </ChartCard>
      </div>

      <div className="col-12 col-lg-4">
        <ChartCard title="Drawdown" right={windowLabel}
          tip={<>
            <div>The "underwater" curve: at each date, how far below its highest prior value the portfolio sat.</div>
            <div style={{ marginTop: 6, color: '#71717a' }}>0% means at a new peak; deeper troughs are bigger losses from the top. Shows both the depth and how long recoveries took.</div>
          </>}>
          <DrawdownChart charts={charts} />
        </ChartCard>
      </div>

      <div className="col-12 col-lg-4">
        <ChartCard title="Daily returns" right={windowLabel}
          tip={<>
            <div>How the portfolio's daily returns are distributed over the window — bar height is the number of days that landed in each return bucket.</div>
            <div style={{ marginTop: 6 }}>The solid <span style={{ color: GOLD }}>Avg</span> line is the mean daily return; the dashed <span style={{ color: GREY }}>VaR 95%</span> and <span style={{ color: GREY }}>VaR 99%</span> lines mark the 1-day historical losses exceeded on only 5% and 1% of days.</div>
            <div style={{ marginTop: 6, color: '#71717a' }}>The x-axis is clipped to a robust range (an IQR fence), so a stray bad price tick can't flatten the chart — a handful of extreme outlier days may be excluded from the bars.</div>
          </>}>
          <DistributionChart charts={charts} />
        </ChartCard>
      </div>
    </div>
  );
};

export default RiskCharts;
